import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ChevronRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { RegioMap } from '../components/RegioMap';
import { OfficeAddressBlock } from '../components/OfficeAddressBlock';
import { werkgebiedPlaces } from '../data/werkgebied';
import { buildBreadcrumbList, buildCollectionSchema } from '../lib/structuredData';
export const WerkgebiedOverviewPage: React.FC = () => {
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-3421" data-mp="2c1eylonk8vjt">
      <SEO title="Werkgebied: online marketing en SEO in Noord-Holland Noord | Noord Digital" description="Noord Digital werkt vanuit Oudkarspel voor ondernemers in Alkmaar, Heerhugowaard, Langedijk, Heiloo, Bergen, Castricum, Schagen, Den Helder en Hoorn." keywords="online marketing noord-holland noord, seo alkmaar, online zichtbaarheid heerhugowaard, lokale seo dijk en waard, webapplicatie laten maken alkmaar" canonical="https://noorddigital.nl/werkgebied" jsonLd={[buildCollectionSchema({
      name: 'Werkgebied van Noord Digital',
      description: 'Plaatsen in Noord-Holland Noord waar Noord Digital voor ondernemers werkt.',
      path: '/werkgebied',
      items: werkgebiedPlaces.map(place => ({
        name: `Online marketing en SEO in ${place.name}`,
        path: `/werkgebied/${place.slug}`
      }))
    }), buildBreadcrumbList([{
      name: 'Werkgebied',
      path: '/werkgebied'
    }])]} data-id="element-3422" data-mp="2gs55j6bmx6gm" />
      <Header data-id="element-3423" data-mp="1q0hj8m8t30r5" />

      <main data-id="element-3424" data-mp="1q22aa1vww1k9">
        <section className="w-full bg-[#F7F8FA] pt-32 pb-20 md:pb-24 border-b border-gray-200" data-id="element-3425" data-mp="1q1j41ifmj1m2">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3426" data-mp="1l37r2yu18azm">
            <nav aria-label="Kruimelpad" className="mb-8" data-id="element-3427" data-mp="3kdz6071dztl4">
              <ol className="flex flex-wrap items-center gap-1.5 text-sm text-[#6B7280]" data-id="element-3428" data-mp="3ffxnbn7vhwa6">
                <li data-id="element-3429" data-mp="3fe4imdgtlm4t">
                  <Link to="/" className="hover:text-[#004AC7] transition-colors duration-200" data-id="element-3430" data-mp="1dr1hci2o2xek">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" data-id="element-3431" data-mp="1ijkytq4h6con">
                  <ChevronRightIcon className="h-3.5 w-3.5 text-gray-300" data-id="element-3432" data-mp="13t3unz0gh8o2" />
                </li>
                <li className="font-medium text-[#111827]" aria-current="page" data-id="element-3433" data-mp="18r5h8tx3bq4a">
                  Werkgebied
                </li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-3434" data-mp="1wfexlgbjblo1">
              <motion.div className="lg:col-span-7" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-3435" data-mp="21e9n1140c8h4">
                <h1 className="font-bold text-[#111827] text-4xl md:text-5xl lg:text-6xl leading-tight mb-6" data-id="element-3436" data-mp="b6117lamzo4o">
                  Online marketing en SEO in Alkmaar en Noord-Holland Noord
                </h1>
                <p className="text-[#4B5563] text-lg leading-relaxed mb-8" data-id="element-3437" data-mp="2uclli7tsr1wu">
                  Ons kantoor staat in Oudkarspel, gemeente Dijk en Waard. Van
                  daaruit werken we voor ondernemers in de hele regio, van
                  Alkmaar en Heerhugowaard tot Den Helder en Hoorn. Kies je
                  plaats om te lezen hoe we daar werken en welke klanten we in de
                  buurt hebben.
                </p>
                <a href="/#contact" className="inline-flex items-center gap-2 bg-[#004AC7] hover:bg-[#003A9E] text-white font-semibold py-4 px-8 rounded-lg shadow-md transition-colors duration-200" data-id="element-3438" data-mp="26lzcoebyxdls">
                  Plan een kennismaking
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-3439" data-mp="2fkl3ewrlfyxn" />
                </a>
              </motion.div>
              <div className="lg:col-span-5" data-id="element-3440" data-mp="1rtxhh7kuf8s3">
                <div className="mx-auto lg:mr-0 max-w-sm rounded-2xl border border-gray-200 bg-white p-6" data-id="element-3441" data-mp="1ntndg0gm2sfv">
                  <RegioMap data-id="element-3442" data-mp="20l8ua7tuv4lh" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-20 md:py-24" data-id="element-3443" data-mp="25jacyrndd1wf">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-3444" data-mp="2ahtyiqy1wbo9">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-3445" data-mp="2f8k7sf9lkyo8">
              <div className="lg:col-span-8" data-id="element-3446" data-mp="2k7x2tnamhc1w">
                <h2 className="font-bold text-[#111827] text-3xl md:text-4xl mb-8" data-id="element-3447" data-mp="2k6vhmpzw796g">Plaatsen in ons werkgebied</h2>
                <ul className="divide-y divide-gray-200 border-y border-gray-200" data-id="element-3448" data-mp="2o7xex1a91b5s">
                  {werkgebiedPlaces.map(place => <li key={place.slug} data-id="element-3449" data-mp="2t9eij4k4bbq2">
                      <Link to={`/werkgebied/${place.slug}`} className="group flex items-center justify-between gap-6 py-5" data-id="element-3450" data-mp="2y87v8urkyyp6">
                        <span data-id="element-3451" data-mp="yvvnc1dk857x">
                          <span className="block font-bold text-[#111827] text-lg group-hover:text-[#004AC7] transition-colors duration-200" data-id="element-3452" data-mp="ywomoha6vsg1">
                            {place.name}
                          </span>
                          <span className="block text-sm text-[#6B7280]" data-id="element-3453" data-mp="rjfm1sli4cf2">
                            {place.municipality} · {place.bereikbaarheid.afstand}, {place.bereikbaarheid.reistijd}
                          </span>
                        </span>
                        <ArrowRightIcon className="h-5 w-5 flex-shrink-0 text-[#004AC7] transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-3454" data-mp="hmknj9p8ac0s" />
                      </Link>
                    </li>)}
                </ul>
                <p className="mt-6 text-sm text-[#6B7280]" data-id="element-3455" data-mp="1a82rwdbi55tp">
                  Afstanden en reistijden zijn benaderingen vanaf ons kantoor in
                  Oudkarspel. Staat jouw plaats er niet bij? We werken ook voor
                  ondernemers daarbuiten.
                </p>
              </div>
              <div className="lg:col-span-4" data-id="element-3456" data-mp="3pyp051gkudcb">
                <OfficeAddressBlock data-id="element-3457" data-mp="3l0x3xv8wg52b" />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-3458" data-mp="2d314lpz43s2h" />
    </div>;
};